package com.havn.app.features.clients.data

import com.havn.app.core.logging.HavnLogger
import com.havn.app.core.media.HavnMediaBucket
import com.havn.app.core.media.HavnPickedPhoto
import com.havn.app.core.media.havnFileExtension
import com.havn.app.core.media.havnPublicStorageSource
import com.havn.app.features.clients.domain.Client
import com.havn.app.features.clients.domain.ClientDraft
import com.havn.app.features.clients.domain.ClientOpening
import com.havn.app.features.clients.domain.ClientRepository
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.postgrest
import io.github.jan.supabase.postgrest.rpc
import io.github.jan.supabase.postgrest.query.Columns
import io.github.jan.supabase.storage.storage
import io.ktor.http.ContentType
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.time.Clock

class SupabaseClientRepository(
    private val client: SupabaseClient
) : ClientRepository {
    private val pageCacheMutex = Mutex()
    private val pageCache = mutableMapOf<SearchPageCacheKey, ClientPageCacheEntry>()

    override suspend fun clientsForAgent(agentId: String, agencyId: String?): Result<List<Client>> {
        return clientPage(agentId, agencyId, query = "", offset = 0, limit = CLIENT_PAGE_SIZE)
    }

    override suspend fun clientPage(
        agentId: String,
        agencyId: String?,
        query: String,
        offset: Int,
        limit: Int
    ): Result<List<Client>> {
        val normalizedQuery = query.trim()
        val cacheKey = SearchPageCacheKey(agentId, agencyId, normalizedQuery, offset, limit)
        return pageCacheMutex.withLock {
            val nowMs = Clock.System.now().toEpochMilliseconds()
            pageCache[cacheKey]
                ?.takeIf { nowMs - it.savedAtMs <= PAGE_CACHE_TTL_MS }
                ?.let { return@withLock Result.success(it.value) }

            val result = runCatching {
                HavnLogger.debug("Clients", "Loading contacts for agent $agentId")
                val rows = client.postgrest.rpc(
                    function = CLIENT_SEARCH_RPC,
                    parameters = SearchPageParams(
                        agencyId = agencyId,
                        query = normalizedQuery.takeIf { it.isNotBlank() },
                        limit = limit,
                        offset = offset
                    )
                )
                    .decodeList<ClientRow>()
                val openingsByClient = openingSummariesForClients(rows.map { it.id })
                rows.map { row -> row.toDomain(openings = openingsByClient[row.id].orEmpty()) }
            }.onFailure {
                HavnLogger.error("Clients", "Failed to load contacts for $agentId", it)
            }
            result.onSuccess { pageCache[cacheKey] = ClientPageCacheEntry(nowMs, it) }
            result
        }
    }

    override suspend fun clientDetail(agentId: String, clientId: String): Result<Client> {
        return runCatching {
            slowSqlDetail("clientDetail(contact_id=$clientId)") {
                HavnLogger.debug("Clients", "Loading contact detail $clientId")
                val row = slowSqlDetail("clientDetail.row(contact_id=$clientId)") {
                    client.from(CLIENTS)
                        .select {
                            filter {
                                eq("id", clientId)
                            }
                            limit(1)
                        }
                        .decodeSingle<ClientRow>()
                }
                val openings = slowSqlDetail("clientDetail.openings(contact_id=$clientId)") {
                    openingsForClient(agentId, clientId)
                }
                row.toDomain(openings = openings)
            }
        }.onFailure {
            HavnLogger.error("Clients", "Failed to load contact $clientId", it)
        }
    }

    private suspend fun <T> slowSqlDetail(label: String, block: suspend () -> T): T {
        val startedAtMs = Clock.System.now().toEpochMilliseconds()
        return try {
            block()
        } finally {
            val elapsedMs = Clock.System.now().toEpochMilliseconds() - startedAtMs
            if (elapsedMs > SLOW_DETAIL_SQL_WARNING_THRESHOLD_MS) {
                HavnLogger.warning("SupabaseSQL", "Slow SQL detail $label took ${elapsedMs}ms")
            }
        }
    }

    private suspend fun openingsForClient(agentId: String, clientId: String): List<ClientOpening> {
        return client.postgrest.rpc(
            function = CLIENT_DETAIL_OPENINGS_RPC,
            parameters = ClientOpeningSummariesParams(
                contactIds = listOf(clientId),
                perContactLimit = CLIENT_DETAIL_OPENING_LIMIT
            )
        )
            .decodeList<ClientOpeningSummaryRow>()
            .map { row ->
                ClientOpening(
                    leadId = row.id,
                    openingId = row.openingId,
                    propertyId = row.propertyId,
                    propertyTitle = row.propertyTitle.orEmpty(),
                    propertyAddress = row.propertyAddress,
                    propertyPhotoDataUrl = null,
                    openingTitle = row.openingTitle,
                    startsAt = row.startsAt.orEmpty(),
                    status = row.status,
                    temperature = row.temperature
                )
            }
    }

    override suspend fun addClient(agentId: String, draft: ClientDraft, agencyId: String?): Result<Client> {
        return runCatching {
            HavnLogger.info("Clients", "Adding contact '${draft.fullName}' for agent $agentId")
            val row = client.from(CLIENTS)
                .insert(
                    ClientInsertRow(
                        agentId = agentId,
                        ownerAgentId = agentId,
                        agencyId = agencyId,
                        firstName = draft.firstName.trim(),
                        lastName = draft.lastName.trim(),
                        fullName = draft.fullName,
                        email = draft.email?.trim()?.takeIf { it.isNotBlank() },
                        phone = draft.phone?.trim()?.takeIf { it.isNotBlank() },
                        notes = draft.notes?.trim()?.takeIf { it.isNotBlank() }
                    )
                ) {
                    select()
                }
                .decodeSingle<ClientRow>()

            clearPageCache()
            row.toDomain()
        }.onFailure {
            HavnLogger.error("Clients", "Failed to add contact '${draft.fullName}'", it)
        }
    }

    override suspend fun updateClient(agentId: String, clientId: String, draft: ClientDraft): Result<Client> {
        return runCatching {
            HavnLogger.info("Clients", "Updating contact $clientId")
            client.from(CLIENTS).update(
                ClientUpdateRow(
                    firstName = draft.firstName.trim(),
                    lastName = draft.lastName.trim(),
                    fullName = draft.fullName,
                    email = draft.email?.trim()?.takeIf { it.isNotBlank() },
                    phone = draft.phone?.trim()?.takeIf { it.isNotBlank() },
                    notes = draft.notes?.trim()?.takeIf { it.isNotBlank() }
                )
            ) {
                filter {
                    eq("id", clientId)
                }
                select()
            }.decodeSingle<ClientRow>()

            clearPageCache()
            clientDetail(agentId, clientId).getOrThrow()
        }.onFailure {
            HavnLogger.error("Clients", "Failed to update contact $clientId", it)
        }
    }

    override suspend fun deleteClient(agentId: String, clientId: String): Result<Unit> {
        return runCatching {
            HavnLogger.warning("Clients", "Deleting contact $clientId")
            client.from(CLIENTS).delete {
                filter {
                    eq("id", clientId)
                }
            }
            clearPageCache()
            Unit
        }.onFailure {
            HavnLogger.error("Clients", "Failed to delete contact $clientId", it)
        }
    }

    override suspend fun updateClientNotes(agentId: String, clientId: String, notes: String): Result<Client> {
        return runCatching {
            HavnLogger.info("Clients", "Updating comments for contact $clientId")
            client.from(CLIENTS).update(ClientNotesUpdateRow(notes = notes.trim().takeIf { it.isNotBlank() })) {
                filter {
                    eq("id", clientId)
                }
                select()
            }.decodeSingle<ClientRow>()

            clearPageCache()
            clientDetail(agentId, clientId).getOrThrow()
        }.onFailure {
            HavnLogger.error("Clients", "Failed to update comments for contact $clientId", it)
        }
    }

    override suspend fun updateClientPhoto(
        agentId: String,
        clientId: String,
        photo: HavnPickedPhoto
    ): Result<Client> {
        return runCatching {
            HavnLogger.info("Clients", "Updating photo for contact $clientId")
            require(photo.bytes.isNotEmpty()) { "Choose a non-empty contact photo." }
            val contentType = photo.contentType.ifBlank { "image/jpeg" }
            val safeName = photo.fileName.sanitizeFileName()
            val storagePath = "$agentId/contacts/$clientId/avatar/${timestampSlug()}-$safeName".withImageExtension(contentType)
            client.storage.from(HavnMediaBucket.ContactPhotos).upload(storagePath, photo.bytes) {
                upsert = true
                this.contentType = ContentType.parse(contentType)
            }
            client.from(CLIENTS).update(
                ClientPhotoUpdateRow(
                    avatarDataUrl = storagePath,
                    avatarStoragePath = storagePath,
                    avatarContentType = contentType
                )
            ) {
                filter {
                    eq("id", clientId)
                }
                select()
            }.decodeSingle<ClientRow>()

            clearPageCache()
            clientDetail(agentId, clientId).getOrThrow()
        }.onFailure {
            HavnLogger.error("Clients", "Failed to update photo for contact $clientId", it)
        }
    }

    override suspend fun sendQrCode(agentId: String, clientId: String): Result<Unit> {
        return runCatching {
            val target = clientDetail(agentId, clientId).getOrThrow()
            HavnLogger.info("Clients", "Prepared QR delivery for ${target.fullName} (${target.contactLine})")
            Unit
        }.onFailure {
            HavnLogger.error("Clients", "Failed to prepare QR delivery for contact $clientId", it)
        }
    }

    private suspend fun clearPageCache() {
        pageCacheMutex.withLock { pageCache.clear() }
    }

    private suspend fun openingSummariesForClients(clientIds: List<String>): Map<String, List<ClientOpening>> {
        if (clientIds.isEmpty()) return emptyMap()
        return client.postgrest.rpc(
            function = CLIENT_OPENING_SUMMARIES_RPC,
            parameters = ClientOpeningSummariesParams(
                contactIds = clientIds,
                perContactLimit = CLIENT_OPENING_SUMMARY_LIMIT_PER_CONTACT
            )
        )
            .decodeList<ClientOpeningSummaryRow>()
            .groupBy(
                keySelector = { it.clientId.orEmpty() },
                valueTransform = { row ->
                    ClientOpening(
                        leadId = row.id,
                        openingId = row.openingId,
                        propertyId = row.propertyId,
                        propertyTitle = row.propertyTitle.orEmpty(),
                        propertyAddress = row.propertyAddress,
                        propertyPhotoDataUrl = null,
                        openingTitle = row.openingTitle,
                        startsAt = row.startsAt.orEmpty(),
                        status = row.status,
                        temperature = row.temperature
                    )
                }
            )
            .filterKeys { it.isNotBlank() }
            .mapValues { (_, openings) -> openings.take(CLIENT_OPENING_SUMMARY_LIMIT_PER_CONTACT) }
    }

    private companion object {
        const val CLIENT_PAGE_SIZE = 100
        const val CLIENT_OPENING_SUMMARY_LIMIT_PER_CONTACT = 2
        const val CLIENT_DETAIL_OPENING_LIMIT = 100
        const val PAGE_CACHE_TTL_MS = 15_000L
        const val SLOW_DETAIL_SQL_WARNING_THRESHOLD_MS = 1_000L
        const val CLIENT_OPENING_SUMMARIES_RPC = "contact_opening_summaries_for_current_user"
        const val CLIENT_DETAIL_OPENINGS_RPC = "contact_detail_openings_for_current_user"
        const val CLIENTS = "contacts"
        const val LEADS = "leads"
        const val OPEN_HOUSES = "open_houses"
        const val PROPERTIES = "properties"
        const val PHOTOS = "property_photos"
        const val CLIENT_SEARCH_RPC = "search_contacts_for_current_user"
        val CLIENT_LIST_COLUMNS = Columns.list(
            "id",
            "agent_id",
            "first_name",
            "last_name",
            "full_name",
            "email",
            "phone",
            "qr_token",
            "wallet_pass_key",
            "notes",
            "avatar_url",
            "avatar_storage_path",
            "avatar_content_type",
            "created_at",
            "updated_at"
        )
    }
}

private fun String.sanitizeFileName(): String =
    lowercase()
        .replace(Regex("[^a-z0-9._-]+"), "-")
        .trim('-', '.', '_')
        .ifBlank { "photo.jpg" }

private fun timestampSlug(): String =
    Clock.System.now().toEpochMilliseconds().toString()

private fun String.withImageExtension(contentType: String): String =
    if (substringAfterLast('/', "").substringAfterLast('.', "").isNotBlank()) this else "$this.${contentType.havnFileExtension()}"

@Serializable
private data class ClientRow(
    val id: String,
    @SerialName("agent_id") val agentId: String,
    @SerialName("first_name") val firstName: String? = null,
    @SerialName("last_name") val lastName: String? = null,
    @SerialName("full_name") val fullName: String,
    val email: String? = null,
    val phone: String? = null,
    @SerialName("qr_token") val qrToken: String,
    @SerialName("wallet_pass_key") val walletPassKey: String,
    val notes: String? = null,
    @SerialName("avatar_url") val avatarDataUrl: String? = null,
    @SerialName("avatar_storage_path") val avatarStoragePath: String? = null,
    @SerialName("avatar_content_type") val avatarContentType: String? = null,
    @SerialName("created_at") val createdAt: String? = null,
    @SerialName("updated_at") val updatedAt: String? = null
) {
    fun toDomain(openings: List<ClientOpening> = emptyList()): Client = Client(
        id = id,
        agentId = agentId,
        firstName = firstName,
        lastName = lastName,
        fullName = fullName,
        email = email,
        phone = phone,
        qrToken = qrToken,
        walletPassKey = walletPassKey,
        notes = notes,
        avatarDataUrl = havnPublicStorageSource(HavnMediaBucket.ContactPhotos, avatarStoragePath ?: avatarDataUrl),
        avatarContentType = avatarContentType,
        createdAt = createdAt,
        updatedAt = updatedAt,
        openings = openings
    )
}

@Serializable
private data class ClientInsertRow(
    @SerialName("agent_id") val agentId: String,
    @SerialName("owner_agent_id") val ownerAgentId: String,
    @SerialName("agency_id") val agencyId: String? = null,
    @SerialName("first_name") val firstName: String,
    @SerialName("last_name") val lastName: String,
    @SerialName("full_name") val fullName: String,
    val email: String? = null,
    val phone: String? = null,
    val notes: String? = null
)

@Serializable
private data class ClientUpdateRow(
    @SerialName("first_name") val firstName: String,
    @SerialName("last_name") val lastName: String,
    @SerialName("full_name") val fullName: String,
    val email: String? = null,
    val phone: String? = null,
    val notes: String? = null
)

@Serializable
private data class ClientNotesUpdateRow(
    val notes: String? = null
)

@Serializable
private data class ClientPhotoUpdateRow(
    @SerialName("avatar_url") val avatarDataUrl: String,
    @SerialName("avatar_storage_path") val avatarStoragePath: String,
    @SerialName("avatar_content_type") val avatarContentType: String? = null
)

@Serializable
private data class SearchPageParams(
    @SerialName("p_agency_id") val agencyId: String?,
    @SerialName("p_query") val query: String?,
    @SerialName("p_limit") val limit: Int,
    @SerialName("p_offset") val offset: Int
)

private data class SearchPageCacheKey(
    val agentId: String,
    val agencyId: String?,
    val query: String,
    val offset: Int,
    val limit: Int
)

private data class ClientPageCacheEntry(
    val savedAtMs: Long,
    val value: List<Client>
)

@Serializable
private data class ClientOpeningSummariesParams(
    @SerialName("p_contact_ids") val contactIds: List<String>,
    @SerialName("p_per_contact_limit") val perContactLimit: Int
)

@Serializable
private data class ClientOpeningSummaryRow(
    val id: String,
    @SerialName("contact_id") val clientId: String? = null,
    @SerialName("property_id") val propertyId: String,
    @SerialName("opening_id") val openingId: String,
    val temperature: String,
    val status: String,
    @SerialName("opening_title") val openingTitle: String? = null,
    @SerialName("starts_at") val startsAt: String? = null,
    @SerialName("property_title") val propertyTitle: String? = null,
    @SerialName("property_address_line_1") val propertyAddressLine1: String? = null,
    @SerialName("property_city") val propertyCity: String? = null,
    @SerialName("property_region") val propertyRegion: String? = null
) {
    val propertyAddress: String
        get() = listOfNotNull(
            propertyAddressLine1?.takeIf { it.isNotBlank() },
            propertyCity?.takeIf { it.isNotBlank() },
            propertyRegion?.takeIf { it.isNotBlank() }
        ).joinToString(", ")
}
